// BonBoZ: giữ máy này luôn có đăng ký push, rồi báo server và tải lại tình
// trạng điện thoại. Chạy lúc mở app và MỖI lần quay lại app.
//
// Chrome Android bỏ đăng ký push khi mở app lúc đang chặn, nên "có quyền mà
// chưa có đăng ký" có thể xảy ra bất cứ lúc nào, không chỉ lúc khởi động.
// Đăng ký lại gặp lỗi bận tạm thời của Google (`isPushServiceError`) thì tự
// thử lại sau 5 giây, 30 giây, 2 phút, 5 phút rồi cứ 5 phút một lần cho tới
// khi được (đã gặp Google từ chối liền 7 phút). Dừng khi hết quyền, tự tắt
// hoặc máy đã có đăng ký; mỗi lần quay lại app bắt đầu lại từ đầu.
//
// Mốc 30 giây chỉ để đổi tab liên tục không dội request. Máy vừa đổi trạng
// thái (chặn/bỏ chặn, vừa đăng ký được) thì bỏ qua mốc, báo và hỏi lại ngay.
//
// App Android cài ra màn hình chính (WebAPK) chỉ chép quyền thông báo của hệ
// điều hành vào trang ở một bước chạy SAU khi mở app, nên lúc mới mở
// `Notification.permission` có thể còn là giá trị cũ. Vì vậy trên Android,
// sau mỗi lần mở/quay lại app còn đọc lại ở giây 2, 5 và 10; thấy khác thì xử
// lý như vừa đổi quyền. Không thấy khác thì không làm gì.
import { computed, ref } from 'vue';
import { onPageHidden, onPageVisible } from './pageVisibility';
import { isAndroidDevice } from './deviceIdentity';
import {
  hasPushOptOut,
  isPushServiceError,
  restorePushSubscription,
} from './pushHelper';
import {
  hasChangedSinceLastReport,
  reportDeviceStatus,
} from './pushDeviceStatus';
import { refreshPushHealth } from './pushHealth';
import {
  devicePushState,
  refreshDevicePermission,
  refreshDevicePushState,
} from './pushDeviceState';

// Mốc cuối lặp lại mãi cho tới khi đăng ký được.
export const RETRY_DELAYS_MS = [
  5 * 1000,
  30 * 1000,
  2 * 60 * 1000,
  5 * 60 * 1000,
];
export const ANDROID_RECHECK_DELAYS_MS = [2 * 1000, 5 * 1000, 10 * 1000];

let pendingRestore = null;
let retryTimer = null;
let retryIndex = 0;
let recheckTimers = [];

// Đang tự đăng ký lại, tính cả lượt cập nhật sau khi thành công.
const isResubscribing = ref(false);
const isRetryScheduled = ref(false);
const isRecheckWindowOpen = ref(false);

// Có quyền, chắc chắn chưa có đăng ký và không tự tắt: app sẽ tự đăng ký lại.
const needsResubscribe = () => {
  const { permission, hasSubscription } = devicePushState.value;
  return (
    permission === 'granted' && hasSubscription === false && !hasPushOptOut()
  );
};

// App đang tự kết nối lại thông báo cho máy này: đang đăng ký, đang chờ thử
// lại, hoặc còn trong khoảng đọc lại của Android. Banner và Cài đặt hiện
// "Đang kết nối lại" thay cho "đang tắt"/"đã ngừng nhận". Cờ tự tắt không
// reactive, nhưng mỗi lần bật/tắt đều đọc lại trạng thái máy nên vẫn tính lại.
export const isReconnecting = computed(
  () =>
    isResubscribing.value ||
    ((isRetryScheduled.value || isRecheckWindowOpen.value) &&
      needsResubscribe())
);

export const cancelRestoreRetry = () => {
  clearTimeout(retryTimer);
  retryTimer = null;
  retryIndex = 0;
  isRetryScheduled.value = false;
};

// Sau mỗi lần bật/tắt (bấm tay, hoặc app tự đăng ký lại được): cập nhật trạng
// thái máy này cho banner và Cài đặt, báo server, tải lại tình trạng — không
// chờ mốc 30 giây.
export const syncAfterPushChange = async () => {
  await refreshDevicePushState();
  await reportDeviceStatus({ force: true });
  await refreshPushHealth({ force: true });
};

// Đăng ký lại được thì cập nhật mọi nơi ngay trong lượt này, để "Đang kết nối
// lại" chỉ tắt khi Cài đặt và banner đã có kết quả mới.
const runRestore = options => {
  pendingRestore ||= (async () => {
    isResubscribing.value = needsResubscribe();
    try {
      const result = await restorePushSubscription(options);
      if (result.attempted && !result.error) await syncAfterPushChange();
      return result;
    } finally {
      isResubscribing.value = false;
      pendingRestore = null;
    }
  })();
  return pendingRestore;
};

// Lần thử theo lịch: báo server ngay kết quả, không chờ mốc 30 giây (thành
// công thì `runRestore` đã báo).
const retryRestore = async () => {
  retryTimer = null;
  isRetryScheduled.value = false;
  // eslint-disable-next-line no-use-before-define
  const { attempted, error } = await restoreWithRetry({ fromSchedule: true });
  if (error) {
    await reportDeviceStatus({ force: true });
  } else if (!attempted) {
    await syncAfterPushChange();
  }
};

// Chỉ một hẹn giờ: hẹn mới thay hẹn cũ.
const scheduleRetry = () => {
  const delay =
    RETRY_DELAYS_MS[Math.min(retryIndex, RETRY_DELAYS_MS.length - 1)];
  clearTimeout(retryTimer);
  retryTimer = setTimeout(retryRestore, delay);
  retryIndex += 1;
  isRetryScheduled.value = true;
};

// Không bao giờ chạy hai lượt cùng lúc: gọi khi đang chạy thì nhận lại lượt đó.
// Lượt không theo lịch (mở app, quay lại app) huỷ lịch cũ và đếm lại từ 5 giây.
// Không còn cần đăng ký lại (hết quyền, tự tắt, đã có đăng ký) thì
// `restorePushSubscription` không thử, lịch dừng.
export const restoreWithRetry = async ({
  fromSchedule = false,
  resendExisting = false,
} = {}) => {
  if (pendingRestore) return pendingRestore;
  if (!fromSchedule) cancelRestoreRetry();

  const result = await runRestore({ resendExisting });
  if (result.attempted && isPushServiceError(result.error)) {
    scheduleRetry();
  } else {
    cancelRestoreRetry();
  }
  return result;
};

// Thứ tự quan trọng: đăng ký lại xong mới báo trạng thái, kẻo server thấy
// "mất đăng ký" đúng lúc app đang tự đăng ký; báo xong mới hỏi tình trạng.
// Quyền được đọc lại trước tiên, để banner đổi ngay theo máy; đăng ký được đọc
// trước khi tự đăng ký lại, để banner và Cài đặt biết là đang kết nối lại.
export const syncPushState = async ({ resendExisting = false } = {}) => {
  refreshDevicePermission();
  const state = await refreshDevicePushState();
  const { attempted, error } = await restoreWithRetry({ resendExisting });
  // Đăng ký lại được thì `runRestore` đã báo server và tải lại tình trạng.
  if (attempted && !error) return;
  const force = hasChangedSinceLastReport(state);
  await reportDeviceStatus({ force });
  await refreshPushHealth({ force });
};

export const cancelAndroidRecheck = () => {
  recheckTimers.forEach(clearTimeout);
  recheckTimers = [];
  isRecheckWindowOpen.value = false;
};

// Đọc lại quyền và đăng ký; khác trạng thái đang dùng thì tự đăng ký lại (nếu
// cần), rồi báo server và tải lại tình trạng ngay.
const recheckDeviceState = async () => {
  const before = devicePushState.value;
  const after = await refreshDevicePushState();
  if (
    after.permission === before.permission &&
    after.hasSubscription === before.hasSubscription
  ) {
    return;
  }
  if (needsResubscribe()) {
    const { attempted, error } = await restoreWithRetry();
    if (attempted && !error) return;
  }
  await syncAfterPushChange();
};

// Lịch mới thay lịch cũ. Lần đọc cuối xong mới đóng khoảng đọc lại, trừ khi
// lịch đã bị huỷ hoặc thay trong lúc chờ.
const startAndroidRecheck = () => {
  cancelAndroidRecheck();
  const lastIndex = ANDROID_RECHECK_DELAYS_MS.length - 1;
  const timers = ANDROID_RECHECK_DELAYS_MS.map((delay, index) =>
    setTimeout(async () => {
      await recheckDeviceState();
      if (index === lastIndex && recheckTimers === timers) {
        cancelAndroidRecheck();
      }
    }, delay)
  );
  recheckTimers = timers;
  isRecheckWindowOpen.value = true;
};

// Lúc mở app gửi lại cả đăng ký sẵn có; khi quay lại app thì không cần, vì
// `device_status` đã mang endpoint. Rời màn hình thì huỷ các lần đọc lại còn
// chờ của Android. Trả hàm dừng.
export const startPushSync = () => {
  const recheck = isAndroidDevice() ? startAndroidRecheck : () => {};
  syncPushState({ resendExisting: true });
  recheck();
  const stopVisible = onPageVisible(() => {
    syncPushState();
    recheck();
  });
  const stopHidden = onPageHidden(cancelAndroidRecheck);
  return () => {
    stopVisible();
    stopHidden();
    cancelRestoreRetry();
    cancelAndroidRecheck();
  };
};
